import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import UploadIcon from '@mui/icons-material/Upload';
import * as XLSX from 'xlsx';
import { useNotify, useRefresh } from "react-admin";
import { API } from "../providers/api.provider";
import { Alert, Box, CircularProgress, Link, Typography } from "@mui/material";

/**
 * Formato esperado da planilha XLSX:
 * 
 * A planilha deve conter todas as informações do usuário e da licença em uma única linha.
 * O sistema irá:
 * 1. Verificar se o usuário existe (por CPF/CNPJ ou email)
 * 2. Criar o usuário se não existir
 * 3. Criar a licença vinculada ao usuário
 * 
 * Colunas de usuário (obrigatórias):
 * - nome: Nome completo do usuário
 * - email: Email principal
 * - usuario: Nome de usuário para login
 * - cpf_cnpj: CPF ou CNPJ
 * 
 * Colunas de usuário (opcionais):
 * - telefone: Telefone principal
 * - emails_secundarios: Emails separados por vírgula ou ponto-e-vírgula
 * - telefones: Telefones separados por vírgula ou ponto-e-vírgula
 * - cep: CEP do endereço
 * - logradouro: Rua/Avenida
 * - numero: Número do endereço
 * - complemento: Complemento
 * - bairro: Bairro
 * - cidade: Cidade
 * - estado: Estado (sigla com 2 letras)
 * - empresa_id: ID da empresa (número)
 * - observacoes: Observações adicionais
 * 
 * Colunas de licença (obrigatórias):
 * - produto_id: ID do produto (UUID ou número)
 * - versao: Versão do produto (número)
 * - codigo_serial: Código do serial da licença
 * 
 * Colunas de licença (opcionais):
 * - tempo_uso: Tempo de uso em dias (número)
 * - data_aquisicao: Data de aquisição (formato: YYYY-MM-DD ou DD/MM/YYYY)
 * - ativacoes_contratadas: Número de ativações contratadas (número)
 * - ativacoes_restantes: Número de ativações restantes (número)
 * - codigo_reset: Código de reset
 * - plug: Se é plug (SIM/NÃO ou TRUE/FALSE ou 1/0)
 * - valor_venda: Valor da venda (número)
 * - parcelas_vendas: Número de parcelas (número)
 * - metodo_pagamento: Método de pagamento (texto)
 */

// Limites de importação
const MAX_FILE_SIZE_MB = 5; // 5MB
const MAX_ROWS = 500; // Máximo de linhas por importação

export const ImportUsersLicensesButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validar tamanho do arquivo
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        notify(`Arquivo muito grande (${fileSizeMB.toFixed(2)}MB). Limite: ${MAX_FILE_SIZE_MB}MB`, { 
          type: 'error' 
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  const downloadTemplate = () => {
    // Criar planilha template com todos os campos
    const templateData = [
      {
        // Dados do usuário
        nome: "João da Silva",
        email: "joao@example.com",
        usuario: "joao.silva",
        cpf_cnpj: "123.456.789-01",
        telefone: "11987654321",
        emails_secundarios: "joao.secundario@example.com",
        telefones: "1133334444",
        cep: "01310100",
        logradouro: "Avenida Paulista",
        numero: "1000",
        complemento: "Apto 101",
        bairro: "Bela Vista",
        cidade: "São Paulo",
        estado: "SP",
        empresa_id: "",
        observacoes: "Usuário exemplo",
        
        // Dados da licença
        produto_id: "1c7f931f-acca-457d-a73f-eb3ea65ae9ca", // UUID do produto
        versao: 1,
        codigo_serial: "XXXX-XXXX-XXXX-XXXX",
        tempo_uso: 30,
        data_aquisicao: "2026-01-30",
        ativacoes_contratadas: 1,
        ativacoes_restantes: 1,
        codigo_reset: " ",
        plug: "SIM",
        valor_venda: 1500.00,
        parcelas_vendas: 12,
        metodo_pagamento: "Cartão de Crédito"
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Usuários e Licenças");
    XLSX.writeFile(wb, "template_usuarios_licencas.xlsx");
  };

  const handleImport = async () => {
    if (!file) {
      notify('Selecione um arquivo para importar', { type: 'warning' });
      return;
    }

    setLoading(true);

    try {
      // Criar FormData para enviar o arquivo
      const formData = new FormData();
      formData.append('file', file);

      // Enviar para o backend
      const response = await API.post('/admin/tasks/import-users-licenses-xlsx', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Exibir resultado detalhado
      const result = response.data;
      if (result.success) {
        const successMsg = `Importação concluída!\n` +
          `✓ ${result.usersCreated || 0} usuário(s) criado(s)\n` +
          `✓ ${result.usersFound || 0} usuário(s) encontrado(s)\n` +
          `✓ ${result.licensesCreated || 0} licença(s) criada(s)`;
        
        notify(successMsg, { type: 'success', autoHideDuration: 8000 });
        
        // Exibir avisos se houver
        if (result.warnings && result.warnings.length > 0) {
          setTimeout(() => {
            notify(`Avisos:\n${result.warnings.slice(0, 3).join('\n')}`, { 
              type: 'warning', 
              autoHideDuration: 8000 
            });
          }, 1000);
        }
      }

      setOpen(false);
      setFile(null);
      refresh();
    } catch (error: any) {
      console.error('Erro ao importar usuários e licenças:', error);
      console.error('Detalhes completos do erro:', error?.response?.data);
      
      // Extrair mensagens de erro
      const responseData = error?.response?.data;
      let errorMessage = 'Erro ao importar dados.';
      
      if (responseData) {
        // Se message é um array (múltiplos erros de validação)
        if (Array.isArray(responseData.message)) {
          const errorCount = responseData.message.length;
          console.error(`Total de erros de validação: ${errorCount}`);
          
          // Mostrar apenas os primeiros 10 erros
          const firstErrors = responseData.message.slice(0, 10);
          console.error('Primeiros erros:', firstErrors);
          
          errorMessage = `Erro de validação no backend (${errorCount} problema(s) encontrado(s)):\n\n` +
            firstErrors.join('\n') +
            (errorCount > 10 ? `\n\n...e mais ${errorCount - 10} erro(s). Veja o console (F12) para detalhes completos.` : '');
        } 
        // Se message é string simples
        else if (typeof responseData.message === 'string') {
          errorMessage = responseData.message;
        }
        // Se tem error field
        else if (responseData.error) {
          errorMessage = responseData.error;
        }
        // Se tem errors array
        else if (responseData.errors) {
          errorMessage = JSON.stringify(responseData.errors, null, 2);
        }
      }
      
      notify(errorMessage, { type: 'error', autoHideDuration: 15000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={() => setOpen(true)}
      >
        <UploadIcon style={{ marginRight: 8 }} />
        Importar Usuários + Licenças
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importar Usuários e Licenças</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>Importação combinada de usuários e licenças</strong>
              </Typography>
              <Typography variant="body2" component="div" gutterBottom>
                Este recurso permite importar usuários e suas licenças em uma única operação.
                O sistema irá:
              </Typography>
              <Typography variant="body2" component="div" sx={{ ml: 2, mt: 1 }}>
                1. Verificar se o usuário já existe (por CPF/CNPJ ou email)<br />
                2. Criar o usuário caso não exista<br />
                3. Criar a licença vinculada ao usuário
              </Typography>
              
              <Typography variant="body2" component="div" sx={{ mt: 2 }}>
                <strong>Limites:</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ ml: 2, color: 'warning.main' }}>
                • Tamanho máximo: {MAX_FILE_SIZE_MB}MB<br />
                • Número máximo de linhas: {MAX_ROWS}<br />
                • Se sua planilha for maior, divida em múltiplos arquivos
              </Typography>
              
              <Typography variant="body2" component="div" sx={{ mt: 2 }}>
                <strong>Formato do arquivo:</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ ml: 2 }}>
                • Arquivo Excel (.xlsx)<br />
                • <strong>Campos obrigatórios do usuário:</strong> nome, email, usuario, cpf_cnpj<br />
                • <strong>Campos opcionais do usuário:</strong> telefone, emails_secundarios, telefones, 
                  cep, logradouro, numero, complemento, bairro, cidade, estado, empresa_id, observacoes<br />
                • <strong>Campos obrigatórios da licença:</strong> produto_id, versao, codigo_serial<br />
                • <strong>Campos opcionais da licença:</strong> tempo_uso, data_aquisicao, ativacoes_contratadas, 
                  ativacoes_restantes, codigo_reset, plug, valor_venda, parcelas_vendas, metodo_pagamento
              </Typography>
              
              <Box mt={2}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={downloadTemplate}
                  style={{ cursor: 'pointer' }}
                >
                  📥 Baixar planilha modelo
                </Link>
              </Box>
            </Alert>
          </Box>

          <input
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            id="import-users-licenses-file"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="import-users-licenses-file">
            <Button variant="outlined" component="span" fullWidth disabled={loading}>
              {file ? file.name : 'Selecionar arquivo'}
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            disabled={!file || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
